import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { adminOrdersApi } from '../api/sales';
import { adminBulkApi } from '../api/operations';
import type {
  FinancialStatus,
  FulfillmentStatus,
  OrderQueryDto,
  OrderStatus,
} from '../api/types';
import {
  financialStatusValues,
  fulfillmentStatusValues,
  orderStatusValues,
} from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import { Badge, Pagination, Spinner } from '../components/ui';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDateTime, money, number } from '../lib/format';
import {
  financialStatusLabels,
  fulfillmentStatusLabels,
  labelOf,
  orderStatusLabels,
} from '../lib/labels';
import { P } from '../lib/permissions';

export function OrdersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const [params] = useSearchParams();

  const [query, setQuery] = useState<OrderQueryDto>({
    page: 1,
    pageSize: 20,
    financialStatus: (params.get('durum') as FinancialStatus) ?? undefined,
  });

  const orders = useQuery({
    queryKey: ['admin', 'orders', query],
    queryFn: () => adminOrdersApi.search(query),
    placeholderData: keepPreviousData,
  });

  const exportCsv = useMutation({
    mutationFn: () => adminBulkApi.exportOrders(query),
    onSuccess: (operation) => {
      toast.success('Dışa aktarma başlatıldı.', `İşlem kodu: ${operation.publicId}`);
      navigate('/toplu-islemler');
    },
    onError: (error) => toast.fromError(error, 'Dışa aktarma başlatılamadı.'),
  });

  const columns: Column<NonNullable<typeof orders.data>['items'][number]>[] = [
    {
      key: 'order',
      header: 'Sipariş',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 shrink-0 overflow-hidden rounded bg-ink-50">
            {row.primaryImageUrl ? (
              <img src={row.primaryImageUrl} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <Link
              to={`/siparisler/${row.id}`}
              className="block font-medium text-ink-900 hover:text-brand-700"
            >
              {row.orderNumber}
            </Link>
            <p className="truncate text-xs text-ink-400">{formatDateTime(row.createdAt)}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Müşteri',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-800">{row.customerFullName ?? '—'}</p>
          <p className="truncate text-xs text-ink-400">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'financial',
      header: 'Ödeme',
      cell: (row) => <Badge label={labelOf(financialStatusLabels, row.financialStatus)} />,
    },
    {
      key: 'fulfillment',
      header: 'Sevkiyat',
      cell: (row) => <Badge label={labelOf(fulfillmentStatusLabels, row.fulfillmentStatus)} />,
    },
    {
      key: 'items',
      header: 'Kalem',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => number(row.itemCount),
    },
    {
      key: 'risk',
      header: 'Risk',
      align: 'center',
      hideOnMobile: true,
      cell: (row) =>
        row.riskScore === null || row.riskScore === undefined ? (
          <span className="text-ink-300">—</span>
        ) : (
          <span
            className={`badge ${
              row.riskScore >= 70
                ? 'bg-red-50 text-red-700'
                : row.riskScore >= 40
                  ? 'bg-amber-50 text-amber-800'
                  : 'bg-emerald-50 text-emerald-700'
            }`}
          >
            {row.riskScore}
          </span>
        ),
    },
    {
      key: 'total',
      header: 'Tutar',
      align: 'right',
      cell: (row) => (
        <span className="font-medium text-ink-900">{money(row.grandTotal, row.currencyCode)}</span>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Siparişler"
        description={orders.data ? `${number(orders.data.totalCount)} sipariş` : undefined}
        actions={
          <>
            {can(P.reports.export) ? (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => exportCsv.mutate()}
                disabled={exportCsv.isPending}
              >
                {exportCsv.isPending ? <Spinner className="h-4 w-4" /> : null}
                CSV dışa aktar
              </button>
            ) : null}
            {can(P.orders.create) ? (
              <Link to="/siparisler/yeni" className="btn-primary">
                Manuel sipariş
              </Link>
            ) : null}
          </>
        }
      />

      <Toolbar>
        <SearchInput
          value={query.search ?? ''}
          onChange={(value) => setQuery((c) => ({ ...c, search: value || undefined, page: 1 }))}
          placeholder="Sipariş no, müşteri veya e-posta"
        />

        <SelectFilter
          label="Sipariş durumu"
          value={query.status ?? ''}
          onChange={(value) =>
            setQuery((c) => ({ ...c, status: (value || undefined) as OrderStatus, page: 1 }))
          }
          options={orderStatusValues.map((value) => ({
            value,
            label: orderStatusLabels[value].text,
          }))}
        />

        <SelectFilter
          label="Ödeme"
          value={query.financialStatus ?? ''}
          onChange={(value) =>
            setQuery((c) => ({
              ...c,
              financialStatus: (value || undefined) as FinancialStatus,
              page: 1,
            }))
          }
          options={financialStatusValues.map((value) => ({
            value,
            label: financialStatusLabels[value].text,
          }))}
        />

        <SelectFilter
          label="Sevkiyat"
          value={query.fulfillmentStatus ?? ''}
          onChange={(value) =>
            setQuery((c) => ({
              ...c,
              fulfillmentStatus: (value || undefined) as FulfillmentStatus,
              page: 1,
            }))
          }
          options={fulfillmentStatusValues.map((value) => ({
            value,
            label: fulfillmentStatusLabels[value].text,
          }))}
        />

        <div>
          <label className="label" htmlFor="order-from">
            Başlangıç
          </label>
          <input
            id="order-from"
            type="date"
            className="field py-2 text-sm"
            value={query.from?.slice(0, 10) ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, from: event.target.value || undefined, page: 1 }))
            }
          />
        </div>

        <div>
          <label className="label" htmlFor="order-to">
            Bitiş
          </label>
          <input
            id="order-to"
            type="date"
            className="field py-2 text-sm"
            value={query.to?.slice(0, 10) ?? ''}
            onChange={(event) =>
              setQuery((c) => ({ ...c, to: event.target.value || undefined, page: 1 }))
            }
          />
        </div>

        <label className="mb-1 flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-ink-300 text-brand-600"
            checked={query.hasOpenReturn === true}
            onChange={(event) =>
              setQuery((c) => ({ ...c, hasOpenReturn: event.target.checked || undefined, page: 1 }))
            }
          />
          Açık iadesi olanlar
        </label>
      </Toolbar>

      <DataTable
        columns={columns}
        rows={orders.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={orders.isLoading}
        error={orders.error}
        onRetry={() => void orders.refetch()}
        emptyMessage="Bu filtrelere uyan sipariş yok."
        onRowClick={(row) => navigate(`/siparisler/${row.id}`)}
        footer={
          orders.data ? (
            <Pagination
              page={orders.data.page}
              totalPages={orders.data.totalPages}
              totalCount={orders.data.totalCount}
              onChange={(page) => setQuery((c) => ({ ...c, page }))}
            />
          ) : null
        }
      />
    </div>
  );
}

/* --------------------------- Sevk bekleyenler --------------------------- */

export function PendingFulfillmentPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const pending = useQuery({
    queryKey: ['admin', 'pending-fulfillment', page],
    queryFn: () => adminOrdersApi.pendingFulfillment({ page, pageSize: 20 }),
    placeholderData: keepPreviousData,
  });

  const columns: Column<NonNullable<typeof pending.data>['items'][number]>[] = [
    {
      key: 'order',
      header: 'Sipariş',
      cell: (row) => (
        <div>
          <Link
            to={`/siparisler/${row.id}`}
            className="font-medium text-ink-900 hover:text-brand-700"
          >
            {row.orderNumber}
          </Link>
          <p className="text-xs text-ink-400">{formatDateTime(row.createdAt)}</p>
        </div>
      ),
    },
    {
      key: 'customer',
      header: 'Müşteri',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-800">{row.customerFullName ?? '—'}</p>
          <p className="truncate text-xs text-ink-400">{row.email}</p>
        </div>
      ),
    },
    {
      key: 'financial',
      header: 'Ödeme',
      cell: (row) => <Badge label={labelOf(financialStatusLabels, row.financialStatus)} />,
    },
    {
      key: 'fulfillment',
      header: 'Sevkiyat',
      cell: (row) => <Badge label={labelOf(fulfillmentStatusLabels, row.fulfillmentStatus)} />,
    },
    {
      key: 'items',
      header: 'Kalem',
      align: 'right',
      cell: (row) => number(row.itemCount),
    },
    {
      key: 'total',
      header: 'Tutar',
      align: 'right',
      cell: (row) => money(row.grandTotal, row.currencyCode),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Sevkiyat"
        description="Ödemesi alınmış ve sevk edilmeyi bekleyen siparişler"
      />

      <DataTable
        columns={columns}
        rows={pending.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={pending.isLoading}
        error={pending.error}
        onRetry={() => void pending.refetch()}
        emptyMessage="Sevk bekleyen sipariş yok."
        onRowClick={(row) => navigate(`/siparisler/${row.id}`)}
        footer={
          pending.data ? (
            <Pagination
              page={pending.data.page}
              totalPages={pending.data.totalPages}
              totalCount={pending.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />
    </div>
  );
}
